import { useMutation, useQuery, useQueryClient } from "react-query";
import qs from "qs-lite";

import api from "../../utils/api";
import { useHubSnackBar } from "../useSnackBar";
import { getQueryParams } from "../../helpers";
import { invalidate } from "../../helpers/clientHelper";

const BOXING = "/boxing";
const LINES = "/lineitems";

const postBoxingData = (data) => api.post(BOXING, data);
export const usePostBoxingData = () => {
  const { addSnackbarSuccess } = useHubSnackBar();
  const queryClient = useQueryClient();

  return useMutation(postBoxingData, {
    onSuccess: () => {
      invalidate(queryClient, BOXING);
      invalidate(queryClient, LINES);
      addSnackbarSuccess({ message: "Successfully Created Boxes" });
    },
  });
};

const putBoxingData = (data) => {
  const id = data.id;
  delete data.id;
  return api.put(`${BOXING}/${id}`, data);
};
export const usePutBoxingData = () => {
  const { addSnackbarSuccess } = useHubSnackBar();
  const queryClient = useQueryClient();

  return useMutation(putBoxingData, {
    onSuccess: () => {
      invalidate(queryClient, BOXING);
      invalidate(queryClient, LINES);
      addSnackbarSuccess({ message: "Successfully Updated Boxes" });
    },
  });
};

const getAllBoxing = ({ queryKey }) => {
  const queryString = qs.stringify(queryKey[1]);
  return api.get(`${BOXING}?${queryString}`);
};
export const useGetAllBoxing = ({ locationId = 1, returning = false, shipmentId, enabled = true }) => {
  let include = [
    ["location", locationId],
    ["customer", null],
    ["order", null],
    ["type", null],
    ["returning", returning],
  ];
  if (shipmentId) include = [["shipmentId", shipmentId]]

  const queryProps = getQueryParams({
    defaultOrderBy: "createdAt",
    defaultSortOrder: "desc",
    include,
  });

  return useQuery([BOXING, { ...queryProps }], getAllBoxing, { enabled });
};

const getEditBoxing = ({ queryKey }) => {
  const queryString = qs.stringify(queryKey[2]);
  return api.get(`${BOXING}/edit?${queryString}`);
};
export const useGetEditBoxing = (ids = [], returning = false) => {
  return useQuery([BOXING, "/edit", { ids, returning }], getEditBoxing, {
    enabled: !!ids?.length,
  });
};

const getAllBoxCustomers = () => api.get(`${BOXING}/customers`);
export const useGetAllBoxCustomers = () => {
  return useQuery([BOXING, "/customers"], getAllBoxCustomers);
};

const getAllBoxOrders = () => api.get(`${BOXING}/orders`);
export const useGetAllBoxOrders = () => {
  return useQuery([BOXING, "/orders"], getAllBoxOrders);
};

const postPalletizeBoxes = (payload) =>
  api.post(`${BOXING}/palettize`, payload);
export const usePostPalettizeBoxes = () => {
  const { addSnackbarSuccess } = useHubSnackBar();
  const queryClient = useQueryClient();

  return useMutation(postPalletizeBoxes, {
    onSuccess: () => {
      invalidate(queryClient, BOXING);
      addSnackbarSuccess({ message: "Successfully Created Pallete" });
    },
  });
};

const postSplitBoxes = ({ id, boxes }) =>
  api.post(`${BOXING}/split/${id}`, { boxes });
export const usePostSplitBoxes = () => {
  const { addSnackbarSuccess } = useHubSnackBar();
  const queryClient = useQueryClient();

  return useMutation(postSplitBoxes, {
    onSuccess: () => {
      invalidate(queryClient, BOXING);
      addSnackbarSuccess({ message: "Successfully Split Boxes" });
    },
  });
};

const putMoveBoxes = (payload) => api.put(`${BOXING}/move`, payload);
export const usePutMoveBoxes = () => {
  const queryClient = useQueryClient();
  return useMutation(putMoveBoxes, {
    onSuccess: () => invalidate(queryClient, BOXING),
  });
};

const deleteBoxById = (id) => api.delete(`${BOXING}/${id}`);
export const useDeleteBoxById = () => {
  const { addSnackbarSuccess } = useHubSnackBar();
  const queryClient = useQueryClient();

  return useMutation(deleteBoxById, {
    onSuccess: () => {
      invalidate(queryClient, BOXING);
      addSnackbarSuccess({ message: "Successfully Deleted Box" });
    },
  });
};

const deleteBoxesById = (data) => api.delete(BOXING, { data });
export const useDeleteBoxesById = () => {
  const { addSnackbarSuccess } = useHubSnackBar();
  const queryClient = useQueryClient();

  return useMutation(deleteBoxesById, {
    onSuccess: () => {
      invalidate(queryClient, BOXING);
      addSnackbarSuccess({ message: "Successfully Deleted Boxes" });
    },
  });
};

const getBoxPackingDetails = (id) => api.get(`${BOXING}/${id}`);
export const useGetBoxPackingDetails = (id, overrideEnabled = true) => {
  const enabled = typeof id != "boolean" && !isNaN(Number(id));
  return useQuery([BOXING, "/packing", id], () => getBoxPackingDetails(id), {
    enabled: enabled && overrideEnabled,
  });
};

const getBoxingWorkOrders = ({ queryKey }) => {
  const query = queryKey[2];
  const queryString = qs.stringify(query);
  return api.get(`${BOXING}/work-orders?${queryString}`);
};
export const useGetBoxingWorkOrders = ({
  customerId,
  stationId,
  returning,
  locationId,
  workOrders,
}) => {
  const enabled = returning ? stationId : customerId;
  const workOrderIds = workOrders?.join(",");

  const payload = {
    locationId,
    ...(returning ? { stationId } : { customerId }),
    ...(workOrders?.length && { workOrderIds }),
  };

  const queryKey = [BOXING, "/work-orders", payload];
  return useQuery(queryKey, getBoxingWorkOrders, {
    retry: false,
    enabled: !!enabled,
  });
};